import React, { useContext, type ReactNode } from 'react';
import AdminContext, { type AdminContextType } from '../../../../context/admin';
import { type AuditChangesLanguage } from '../../../../types/language';
import Box from '../../../../atomicDesign/atoms/Box';
import Text from '../../../../atomicDesign/atoms/Text';
import Stack from '../../../../atomicDesign/atoms/Stack';
import DataRow from '../../../../atomicDesign/atoms/DataRow';
import DataCell from '../../../../atomicDesign/atoms/DataCell';
import SimpleTable from '../../../../atomicDesign/molecules/SimpleTable';
import ToneBadge, {
  type BadgeTone,
} from '../../../../atomicDesign/molecules/ToneBadge';

// One recorded change from the backend change tracking
// (util/changeTracking.js), attached to an audit log row as `changes`.
export interface AuditChange {
  _id?: string;
  action: 'create' | 'update' | 'delete' | string;
  resourceType?: string;
  resourceId?: string;
  oldValue?: Record<string, unknown>;
  newValue?: Record<string, unknown>;
  extraInfo?: {
    bulk?: boolean;
    moreDocumentsNotRecorded?: boolean;
    [key: string]: unknown;
  };
}

interface LargeValueSummary {
  _summary: string;
  length?: number;
  keys?: number;
}

const ACTION_TONES: Record<string, BadgeTone> = {
  create: 'success',
  update: 'info',
  delete: 'danger',
};

function actionLabel(action: string, lang: AuditChangesLanguage) {
  if (action === 'create') return lang.created ?? 'Created';
  if (action === 'delete') return lang.deleted ?? 'Deleted';
  return lang.updated ?? 'Updated';
}

function isLargeValueSummary(value: unknown): value is LargeValueSummary {
  return (
    typeof value === 'object' &&
    value !== null &&
    '_summary' in (value as Record<string, unknown>)
  );
}

function formatValue(value: unknown, lang: AuditChangesLanguage): string {
  if (value === undefined || value === null || value === '') return '—';
  if (value === '[redacted]') return lang.hidden ?? 'Hidden';
  if (isLargeValueSummary(value)) {
    const count = value.length ?? value.keys;
    const label = lang.largeValue ?? 'Large value';
    return count === undefined
      ? label
      : `${label} (${count} ${lang.items ?? 'items'})`;
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function changedFields(change: AuditChange): string[] {
  return Array.from(
    new Set([
      ...Object.keys(change.oldValue ?? {}),
      ...Object.keys(change.newValue ?? {}),
    ])
  );
}

/** One changed record: what kind of change, and every field before and after. */
function ChangeEntry({
  change,
  lang,
}: {
  change: AuditChange;
  lang: AuditChangesLanguage;
}) {
  const fields = changedFields(change);
  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <ToneBadge tone={ACTION_TONES[change.action] ?? 'neutral'}>
          {actionLabel(change.action, lang)}
        </ToneBadge>
        <Text variant="body2" sx={{ fontWeight: 600 }}>
          {change.resourceType ?? '-'}
        </Text>
        {change.resourceId && (
          <Text variant="caption" color="text.secondary">
            {change.resourceId}
          </Text>
        )}
      </Stack>
      {fields.length > 0 && (
        <SimpleTable
          columns={[
            { label: lang.field ?? 'Field', width: '30%' },
            { label: lang.before ?? 'Before', width: '35%' },
            { label: lang.after ?? 'After', width: '35%' },
          ]}
          sx={{ mb: 0 }}
        >
          {fields.map((field) => (
            <DataRow key={field}>
              <DataCell sx={{ fontFamily: 'monospace' }}>{field}</DataCell>
              <DataCell sx={{ wordBreak: 'break-word' }}>
                {formatValue(change.oldValue?.[field], lang)}
              </DataCell>
              <DataCell sx={{ wordBreak: 'break-word' }}>
                {formatValue(change.newValue?.[field], lang)}
              </DataCell>
            </DataRow>
          ))}
        </SimpleTable>
      )}
    </Box>
  );
}

/**
 * The expanded audit log row: what the request changed in the database, then
 * the data that was sent (`children`).
 */
export default function AuditRequestDetails({
  changes,
  children,
  read = false,
}: {
  changes?: AuditChange[];
  children?: ReactNode;
  /** a logged read (GET): nothing was changed, show what was asked for */
  read?: boolean;
}) {
  const { language } = useContext(AdminContext) as AdminContextType;
  const lang: AuditChangesLanguage =
    language?.settings?.userManagement?.auditChanges ?? {};
  const list = changes ?? [];
  const moreNotShown = list.some(
    (change) => change.extraInfo?.moreDocumentsNotRecorded
  );

  return (
    <Box sx={{ px: 2.5, py: 1.5, cursor: 'default' }}>
      {!read && (
        <Text variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          {lang.title ?? 'What changed'}
        </Text>
      )}
      {read ? null : list.length === 0 ? (
        <Text variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {lang.empty ?? 'No database changes were recorded for this request.'}
        </Text>
      ) : (
        list.map((change, index) => (
          <ChangeEntry key={change._id ?? index} change={change} lang={lang} />
        ))
      )}
      {moreNotShown && (
        <Text variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {lang.more ?? 'More records were changed than are shown here.'}
        </Text>
      )}
      {children && (
        <>
          <Text variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {read
              ? lang.readQuery ?? 'What was read'
              : lang.sentData ?? 'Data sent'}
          </Text>
          {children}
        </>
      )}
    </Box>
  );
}

/** Short text for the table: "Updated users: level, active". */
export function AuditChangeSummary({ changes }: { changes: AuditChange[] }) {
  const { language } = useContext(AdminContext) as AdminContextType;
  const lang: AuditChangesLanguage =
    language?.settings?.userManagement?.auditChanges ?? {};
  const shown = changes.slice(0, 2).map((change) => {
    const fields = changedFields(change).filter((field) => field !== '_id');
    const base = `${actionLabel(change.action, lang)} ${
      change.resourceType ?? ''
    }`;
    return change.action === 'update' && fields.length > 0
      ? `${base}: ${fields.slice(0, 4).join(', ')}${
          fields.length > 4 ? ' …' : ''
        }`
      : base;
  });
  const rest = changes.length - shown.length;
  return (
    <Text component="span" variant="body2" sx={{ pointerEvents: 'none' }}>
      {shown.join('; ')}
      {rest > 0 ? ` (+${rest})` : ''}
    </Text>
  );
}
